import React, { useCallback, useMemo, useState } from "react";
import { Lock, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import Loader from "../../UI/Loader/Loader";
import ConfirmModal from "../../UI/Modal/confirmModal";
import { useEPMContext } from "../../../contexts/EPMContext";
import { useUserContext } from "../../../contexts/UserContext";
import { permission } from "../../../config/permission";
import ChecklistModal from "./ChecklistModal";
import { CHECKLIST_PROFILE } from "../checklists";

const ChecklistCard = ({
    title,
    note,
    env,
    items,
    onAdd,
    onEdit,
    onDelete,
    canCreate = true,
    canUpdate = true,
    canDelete = true,
}) => {
    const reqCount = useMemo(
        () => items.filter((item) => item.isRequired).length,
        [items],
    );

    return (
        <section
            className="epm-checklists__card"
            aria-label={`${title} checklist`}
        >
            <header className="epm-checklists__card-header">
                <div>
                    <h3>
                        {title}{" "}
                        <span>
                            · {items.length}{" "}
                            {items.length === 1 ? "item" : "items"}
                        </span>
                    </h3>
                    <p>{note}</p>
                </div>
                <span className="epm-checklists__count">
                    {reqCount} required
                </span>
            </header>
            <ol className="epm-checklists__items">
                {items.map((item, index) => (
                    <li key={item._id || item.id || index}>
                        <span className="epm-checklists__index">
                            {index + 1}
                        </span>
                        <div>
                            <strong>{item.name}</strong>
                            {item.description && <p>{item.description}</p>}
                        </div>
                        <div className="epm-checklists__item-actions">
                            <span
                                className={`epm-checklists__req${item.isRequired ? "" : " is-optional"}`}
                            >
                                {item.isRequired ? "REQ" : "OPT"}
                            </span>
                            {canUpdate && (
                                <button
                                    className="stride-table-btn stride-table-btn-edit"
                                    type="button"
                                    title="Edit item"
                                    aria-label={`Edit ${item.name}`}
                                    onClick={() => onEdit(item)}
                                >
                                    <Pencil size={13} />
                                </button>
                            )}
                            {canDelete && (
                                <button
                                    className="stride-table-btn stride-table-btn-delete"
                                    type="button"
                                    title="Delete item"
                                    aria-label={`Delete ${item.name}`}
                                    onClick={() => onDelete(item)}
                                >
                                    <Trash2 size={13} />
                                </button>
                            )}
                        </div>
                    </li>
                ))}
            </ol>
            {canCreate && (
                <button
                    className="epm-checklists__add"
                    type="button"
                    onClick={() => onAdd(env)}
                    aria-label={`Add item to ${title}`}
                >
                    <Plus size={14} /> Add item
                </button>
            )}
        </section>
    );
};

const Checklists = () => {
    const { checklists = [], loading: contextLoading, fetchChecklists, deleteChecklist } = useEPMContext();
    const { checkPermission } = useUserContext();
    const canCreate = checkPermission(permission.XCM_SETTINGS_CHECKLISTS_CREATE);
    const canUpdate = checkPermission(permission.XCM_SETTINGS_CHECKLISTS_UPDATE);
    const canDelete = checkPermission(permission.XCM_SETTINGS_CHECKLISTS_DELETE);

    const [showModal, setShowModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [activeEnv, setActiveEnv] = useState("STAGING");
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const stagingItems = useMemo(
        () =>
            checklists.filter(
                (item) => !item.test_id && (item.env || "").toUpperCase() === "STAGING",
            ),
        [checklists],
    );

    const productionItems = useMemo(
        () =>
            checklists.filter(
                (item) => !item.test_id && (item.env || "").toUpperCase() === "PRODUCTION",
            ),
        [checklists],
    );

    const handleOpenAdd = (env = "STAGING") => {
        setSelectedItem(null);
        setActiveEnv(env);
        setShowModal(true);
    };

    const handleOpenEdit = (item) => {
        setSelectedItem(item);
        setActiveEnv(item.env || "STAGING");
        setShowModal(true);
    };

    const handleSaved = useCallback(async () => {
        setShowModal(false);
        if (fetchChecklists) await fetchChecklists();
    }, [fetchChecklists]);

    const confirmDelete = async () => {
        if (!deleteTarget?._id && !deleteTarget?.id) return;

        setDeleteLoading(true);
        try {
            await deleteChecklist(deleteTarget._id || deleteTarget.id);
            toast.success("Checklist item deleted successfully.");
            setDeleteTarget(null);
        } catch (err) {
            toast.error(err?.message || "Unable to delete checklist item.");
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <div className="epm-checklists">
            {contextLoading && <Loader />}
            <div className="epm-settings__section-heading">
                <div>
                    <h2>Checklists</h2>
                    <p>
                        The org template that every test's Build & QA step
                        copies when the test enters QA. Tests already in QA keep
                        their snapshot; edits apply from the next test on.
                    </p>
                </div>
                <div className="epm-tags__heading-actions">
                    <span>Profile: {CHECKLIST_PROFILE}</span>
                </div>
            </div>

            <div className="epm-checklists__grid">
                <ChecklistCard
                    title="Staging"
                    note="Signed off in staging before any production check opens."
                    env="STAGING"
                    items={stagingItems}
                    onAdd={handleOpenAdd}
                    onEdit={handleOpenEdit}
                    onDelete={setDeleteTarget}
                    canCreate={canCreate}
                    canUpdate={canUpdate}
                    canDelete={canDelete}
                />
                <ChecklistCard
                    title="Production"
                    note="Re-opened automatically when the Experience changes after sign-off."
                    env="PRODUCTION"
                    items={productionItems}
                    onAdd={handleOpenAdd}
                    onEdit={handleOpenEdit}
                    onDelete={setDeleteTarget}
                    canCreate={canCreate}
                    canUpdate={canUpdate}
                    canDelete={canDelete}
                />
            </div>

            <p className="epm-checklists__footnote mt-4 flex items-center gap-2 text-xs text-stride-muted">
                <Lock size={13} className="shrink-0 text-stride-muted" />
                <span>
                    REQ items gate the step: a test can't move to Ready with one open. OPT items are recorded but never block. A test can add one-off checks of its own; they show as ADDED.
                </span>
            </p>

            {showModal && (
                <ChecklistModal
                    checklist={selectedItem}
                    defaultEnv={activeEnv}
                    onClose={() => setShowModal(false)}
                    onSaved={handleSaved}
                />
            )}

            {deleteTarget && (
                <ConfirmModal
                    title="Delete Checklist Item"
                    description={`Are you sure you want to delete the “${deleteTarget.name}” checklist item?`}
                    ConfirmText="Delete"
                    loading={deleteLoading}
                    hideModal={() => setDeleteTarget(null)}
                    handleConfirmation={confirmDelete}
                />
            )}
        </div>
    );
};

export default Checklists;
